import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import { calculateMatchScores } from '../lib/matching';
import { CURRENT_CARNIVAL_YEAR } from '../lib/config';
import type { Band, Swipe, SwipeAction } from '../types/database';

interface MatchedBand {
  band: Band;
  totalScore: number;
  breakdown: {
    budget: number;
    vibe: number;
    music: number;
    crowd: number;
    friends: number;
  };
  reasons: string[];
}

export function useBands() {
  const { profile, user, loading: authLoading } = useAuth();
  const [bands, setBands] = useState<Band[]>([]);
  const [matchedBands, setMatchedBands] = useState<MatchedBand[]>([]);
  const [swipedBandIds, setSwipedBandIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Wait for auth to finish loading first
    if (authLoading) return;

    const userId = user?.id;

    // No user = not authenticated, stop loading (ProtectedRoute will redirect)
    if (!userId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch all active bands (bands are persistent entities across years)
        const { data: bandsData, error: bandsError } = await supabase
          .from('bands')
          .select('*')
          .eq('is_active', true);

        if (bandsError) throw bandsError;

        // Fetch user's existing swipes
        const { data: swipesData, error: swipesError } = await supabase
          .from('swipes')
          .select('band_id')
          .eq('user_id', userId)
          .eq('carnival_year', CURRENT_CARNIVAL_YEAR);

        if (swipesError) throw swipesError;

        const swiped = new Set(swipesData?.map((s) => s.band_id) || []);
        setSwipedBandIds(swiped);

        // Fetch friend matches for friend score calculation
        const { data: friendships } = await supabase
          .from('friendships')
          .select('friend_id')
          .eq('user_id', userId);

        const friendIds = friendships?.map((f) => f.friend_id) || [];

        let friendMatches: { bandId: string; count: number }[] = [];

        if (friendIds.length > 0) {
          const { data: friendSwipes } = await supabase
            .from('swipes')
            .select('band_id')
            .in('user_id', friendIds)
            .in('action', ['match', 'connect'])
            .eq('carnival_year', CURRENT_CARNIVAL_YEAR);

          if (friendSwipes) {
            const counts: Record<string, number> = {};
            friendSwipes.forEach((s) => {
              counts[s.band_id] = (counts[s.band_id] || 0) + 1;
            });
            friendMatches = Object.entries(counts).map(([bandId, count]) => ({
              bandId,
              count,
            }));
          }
        }

        if (cancelled) return;

        setBands(bandsData as Band[]);

        // Calculate match scores if profile exists
        if (profile) {
          const scores = calculateMatchScores(
            profile,
            bandsData as Band[],
            friendMatches
          );
          setMatchedBands(scores);
        } else {
          setMatchedBands(
            (bandsData as Band[]).map((band) => ({
              band,
              totalScore: 50,
              breakdown: { budget: 50, vibe: 50, music: 50, crowd: 50, friends: 0 },
              reasons: [],
            }))
          );
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to fetch bands');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [user?.id, authLoading]); // Run when auth finishes or user changes

  // Get bands that haven't been swiped yet
  const unswipedBands = matchedBands.filter(
    (m) => !swipedBandIds.has(m.band.id)
  );

  return {
    bands,
    matchedBands,
    unswipedBands,
    swipedBandIds,
    loading,
    error,
  };
}

export function useSwipes() {
  const { user, loading: authLoading } = useAuth();
  const [userMatches, setUserMatches] = useState<(Swipe & { band: Band })[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchMatches = async () => {
    const userId = user?.id;
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const { data, error } = await supabase
      .from('swipes')
      .select('*, band:bands(*)')
      .eq('user_id', userId)
      .in('action', ['match', 'connect'])
      .eq('carnival_year', CURRENT_CARNIVAL_YEAR)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setUserMatches(data as (Swipe & { band: Band })[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user?.id) {
      setLoading(false);
      return;
    }
    fetchMatches();
  }, [user?.id, authLoading]);

  const recordSwipe = async (
    bandId: string,
    action: SwipeAction,
    timeSpent?: number,
    expandedProfile?: boolean
  ) => {
    if (!user) return { error: new Error('Not authenticated') };

    const { error } = await supabase.from('swipes').upsert(
      {
        user_id: user.id,
        band_id: bandId,
        action,
        time_spent_seconds: timeSpent,
        expanded_profile: expandedProfile || false,
        carnival_year: CURRENT_CARNIVAL_YEAR,
      },
      {
        onConflict: 'user_id,band_id,carnival_year',
      }
    );

    if (!error) {
      await supabase.from('analytics_events').insert({
        band_id: bandId,
        user_id: user.id,
        event_type: action,
        carnival_year: CURRENT_CARNIVAL_YEAR,
        metadata: { time_spent: timeSpent, expanded: expandedProfile },
      });
    }

    return { error };
  };

  return {
    userMatches,
    loading,
    recordSwipe,
    refetch: fetchMatches,
  };
}
